import { Grid } from "@mui/material";
import loyaltyV2Icon from "assets/loyalty-icon-v2.png";
import { tutorialAssets } from "assets/cdn";
import classnames from "classnames";
import Button from "components/Buttons/ThemeButton";
import { Shine } from "components/PopUpManager/lib/Tutorial/styles";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useEnvContext } from "contexts";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { IShopPurchaseOfferSingleItem } from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useAppSelector, useImagePath } from "hooks";
import useRewardsById from "hooks/useRewardsById";
import { WsDialogs } from "interfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	onDIPtrigger,
	onOpenPopUp,
	selectPopUpManagerState
} from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
// import { CardFinalMessage } from "styles/general_styles";
import { InfoIcon } from "utils/svgIcons";
import { FirstPurchaseBanner } from "./lib/FirstPurchaseBanner";
import { BonusFlag } from "./lib/Flag";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
import {
	CrossOutLine,
	InfoButton,
	RewardContainer,
	RewardImage,
	RewardNumber,
	StyledCard,
	WebBonusText
} from "./styled";

const OfferCardV2 = ({
	data,
	itemRef
}: {
	data: IShopPurchaseOfferSingleItem;
	itemRef?: React.MutableRefObject<HTMLDivElement | null>;
}): JSX.Element => {
	const { getImage } = useImagePath("cdn");
	const shine = getImage(tutorialAssets.shine);
	const {
		bannerText,
		campaignGroup,
		//description,
		firstPurchaseRewardBonus,
		id: offerId,
		loyaltyPointCrossout,
		loyaltyPointReward,
		onlyNonPayers,
		price,
		priority,
		purchaseLimit,
		rewardCrossout,
		rewardId,
		rewardImageUrl,
		sectionName,
		upsellOfferConfig
	} = data;
	const {
		authToken,
		currentOffer,
		handleRefresh,
		openLoginToContinueDialog,
		postUserMetadata,
		setActiveItemToBeClicked,
		setCurrentOffer,
		store,
		suppressOrderConfirmation,
		userTracker,
		wsStoreBuy
	} = useWebStoreContext();
	const { stack, open } = usePopUpsV2();
	const buyButtonRef = useRef(null);
	const dispatch = useAppDispatch();
	const { openDialog } = useDialogModalRedux();
	const displayRewards = useRewardsById(rewardId);
	const { isFeatureFlagEnabled } = useEnvContext();
	const { variant } = useAppSelector(selectPopUpManagerState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );

	const [isCurrentOffer, setIsCurrentOffer] = useState<boolean>(false);

	const isTutorialOpen = useMemo(() => {
		return variant === "tutorial" || stack?.[0]?.variant === "tutorial-v2";
	}, [variant, stack]);

	const resetCurrentOffer = () => {
		setIsCurrentOffer(false);
		setCurrentOffer(null);
	};

	const cbClose = () => {
		resetCurrentOffer();
		reportEvent("dialog_x_closed", { source: "xsolla_dialog" });
		handleRefresh();
		dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
	};

	const handleInfoButtonClick = () => {
		if (isTutorialOpen) {
			return;
		}
		dispatch(onOpenPopUp(`single-item-info::${offerId}`));
		reportEvent("single_items", {
			source: "info_button_click",
			offer_id: offerId
		});
	};

	const firstPurchaseText = useMemo(() => {
		return (
			firstPurchaseRewardBonus &&
			store?.globalConfig
				?.find((c) => c.id === "first_purchase_badge_text")
				?.value?.replace(
					"{amount}",
					Number(firstPurchaseRewardBonus).toLocaleString("en-US")
				)
		);
	}, [firstPurchaseRewardBonus, store?.globalConfig]);

	const onClickBuy = async () => {
		if (isTutorialOpen) {
			return;
		}
		if (upsellOfferConfig && !firstPurchaseRewardBonus) {
			open({
				variant: `bundle-up::${offerId}`,
				id: `bundle-up::${offerId}`,
				replaceIfExists: true
			});
			return;
		}
		// setCurrentOffer used for button states
		setCurrentOffer(buyButtonRef.current);
		if (authToken?.value) {
			const invoice = await wsStoreBuy({ offerId });
			if (invoice) {
				const timestampMs = new Date().getTime();
				openDialog(WsDialogs.XSOLLA_PURCHASE, {
					xsollaToken: invoice.xsollaToken,
					xsollaHost: invoice.xsollaHost,
					invoice,
					source: "offer_card",
					triggerType: displayRewards?.[0]?.id,
					pricePoint: price,
					cbClose,
					suppressOrderConfirmation,
					postUserMetadata,
					showClose: true,
					onPurchaseStarted: (data) => {
						reportEvent("purchase_started", {
							tuid: userTracker?.id as string,
							offer_id: offerId,
							reward_id: rewardId,
							priority: priority,
							price_in_cents: Math.round(price * 100),
							campaign_group: campaignGroup,
							section_name: sectionName,
							only_non_payers: onlyNonPayers,
							loyalty_point_crossout:
								typeof loyaltyPointCrossout !== "undefined",
							items_crossout: typeof rewardCrossout !== "undefined",
							firstPurchaseText,
							first_purchase_bonus: firstPurchaseText,
							invoice_id: invoice?.id,
							paystation_id: data?.payStation
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_started",
							event_type: "web_paystation_purchase",
							action: "purchase_started",
							section_name: sectionName,
							purchase_flow_id: data?.payStation,
							session_engaged: authToken?.value ? "1" : "0",
							loyalty_point_crossout:
								typeof loyaltyPointCrossout !== "undefined",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							items_crossout: typeof rewardCrossout !== "undefined",
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: priority,
							invoice_id: invoice?.id
						});
					},
					onPurchaseSuccess: (data) => {
						reportEvent("purchase_success", {
							invoice_id: invoice?.id
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_success",
							event_type: "web_paystation_purchase",
							action: "purchase_success",
							section_name: sectionName,
							purchase_flow_id: data?.version,
							session_engaged: authToken?.value ? "1" : "0",
							loyalty_point_crossout:
								typeof loyaltyPointCrossout !== "undefined",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							items_crossout: typeof rewardCrossout !== "undefined",
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: priority,
							invoice_id: invoice?.id,
							transaction_id: data?.purchase_invoice_id?.toString()
						});
					}
				});
			} else {
				resetCurrentOffer();
			}

			//NOTE: bfloyd error cases already handled for undefined/null invoice
		} else {
			// setActiveItemToBeClicked used for re-opening after authentication
			setActiveItemToBeClicked({
				type: "store_offer",
				offerId,
				onlyNonPayers,
				purchaseLimit
			});
			openLoginToContinueDialog();
		}
	};

	useEffect(() => {
		if (currentOffer === buyButtonRef.current) {
			setIsCurrentOffer(true);
		} else {
			setIsCurrentOffer(false);
		}
	}, [currentOffer]);

	return (
		<Grid item position="relative" id={offerId} data-tutorial-single-item-offer>
			<Shine id={`${offerId}-offer-card-shine`} src={shine} width={354} />
			{bannerText && <BonusFlag text={bannerText} />}
			<InfoButton aria-label="open info popup" onClick={handleInfoButtonClick}>
				<InfoIcon width={24} height={24} />
			</InfoButton>
			<StyledCard ref={itemRef}>
				<Grid container spacing={0.4} justifyContent="center" mt={4.8} mb={1.2}>
					<Grid item xs={6} display="flex" justifyContent="center">
						<RewardContainer>
							{isFeatureFlagEnabled(FeaturesFlags.FIRST_PURCHASE_BONUS) &&
								firstPurchaseText && (
									<FirstPurchaseBanner
										firstPurchaseText="FIRST PURCHASE"
										firstPurchaseRewardBonus={`+${new Intl.NumberFormat(
											"en-US",
											{
												notation: "compact",
												compactDisplay: "short",
												maximumFractionDigits: 1,
												roundingMode: "floor"
											}
										).format(Number(firstPurchaseRewardBonus))}`}
									/>
								)}
							<RewardImage src={rewardImageUrl} />
							{rewardCrossout && (
								<RewardNumber
									className="cross-out"
									style={{ paintOrder: "stroke fill" }}
								>
									{Number(rewardCrossout.replace(/[^0-9]/g, "")).toLocaleString(
										"en-US"
									)}
									<CrossOutLine />
								</RewardNumber>
							)}
							<RewardNumber style={{ paintOrder: "stroke fill" }}>
								{Number(
									displayRewards?.[0]?.description?.replace(/[^0-9]/g, "")
								).toLocaleString("en-US")}
							</RewardNumber>
						</RewardContainer>
					</Grid>
					<Grid item xs={6} display="flex" justifyContent="center">
						<RewardContainer>
							<WebBonusText>Web bonus:</WebBonusText>
							<RewardImage
								src={loyaltyV2Icon}
								isLp={true}
								alt="Loyalty points icon"
							/>
							{loyaltyPointCrossout && (
								<RewardNumber
									className="cross-out"
									style={{ paintOrder: "stroke fill" }}
								>
									{Number(loyaltyPointCrossout).toLocaleString("en-US")}
									<CrossOutLine />
								</RewardNumber>
							)}
							<RewardNumber style={{ paintOrder: "stroke fill" }}>
								{Number(loyaltyPointReward).toLocaleString("en-US")}
							</RewardNumber>
						</RewardContainer>
					</Grid>
				</Grid>
				<Grid item position="relative" id={`${offerId} button`}>
					<Shine
						id={`${offerId}-offer-card-button-shine`}
						src={shine}
						width={200}
					/>
					<Button
						id={`${offerId}-button`}
						ref={buyButtonRef}
						variant="contained"
						color="success"
						size="large"
						onClick={onClickBuy}
						sx={{
							width: "100%",
							paddingTop: "0.5rem",
							paddingBottom: "0.5rem"
						}}
						className={classnames({
							isCurrentOffer,
							isCurrentOfferSet: !!currentOffer
						})}
					>
						{price} USD
					</Button>
				</Grid>
				{/*
					{isPoisActive && (
						<CardFinalMessage color="#000" opacity={0.6} className="mt">
							Contribute to Poi’s Bounty
						</CardFinalMessage>
					)}
				*/}
				<DebugCopyIdButton
					id={offerId}
					label="ID"
					side="right"
					vertical="top"
					style={{
						fontSize: "12px",
						right: "32px",
						zIndex: 1
					}}
				/>
			</StyledCard>
		</Grid>
	);
};

OfferCardV2.displayName = "OfferCard";

export { OfferCardV2 };
